import * as styles from "./PdfViewerPages.css";

import { type JSX, useCallback, useEffect, useRef } from "react";

import { GlobalPointerProvider } from "@embedpdf/plugin-interaction-manager/react";
import { Scroller } from "@embedpdf/plugin-scroll/react";
import {
  useViewportPlugin,
  useViewportRef,
} from "@embedpdf/plugin-viewport/react";
import { useZoomPlugin } from "@embedpdf/plugin-zoom/react";
import {
  ScrollArea,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from "@frontend/components/atoms/ScrollArea";

import { PdfPage } from "./PdfPage";
import { usePdfViewerContext } from "./PdfViewer.context";

export function PdfViewerPages(): JSX.Element {
  const { documentId, thumbnailsOpen } = usePdfViewerContext();
  const { plugin: zoomPlugin } = useZoomPlugin();
  const { plugin: viewportPlugin } = useViewportPlugin();
  const viewportRef = useViewportRef(documentId);

  const refitZoom = useCallback(() => {
    if (zoomPlugin === null) {
      return;
    }

    const zoom = zoomPlugin.provides().forDocument(documentId);

    const { zoomLevel } = zoom.getState();

    if (typeof zoomLevel === "string") {
      zoom.requestZoom(zoomLevel);
    }
  }, [zoomPlugin, documentId]);

  const pendingRefit = useRef(true);

  useEffect(() => {
    pendingRefit.current = true;
  }, [thumbnailsOpen]);

  useEffect(() => {
    if (viewportPlugin === null) {
      return;
    }

    return viewportPlugin.provides().onViewportResize((event) => {
      if (event.documentId !== documentId || !pendingRefit.current) {
        return;
      }

      pendingRefit.current = false;

      refitZoom();
    });
  }, [viewportPlugin, documentId, refitZoom]);

  return (
    <ScrollArea className={styles.root}>
      <ScrollAreaViewport ref={viewportRef} className={styles.viewport}>
        <GlobalPointerProvider documentId={documentId}>
          <div className={styles.surface}>
            <Scroller
              documentId={documentId}
              renderPage={(page) => <PdfPage {...page} />}
            />
          </div>
        </GlobalPointerProvider>
      </ScrollAreaViewport>

      <ScrollAreaScrollbar orientation="vertical">
        <ScrollAreaThumb />
      </ScrollAreaScrollbar>

      <ScrollAreaScrollbar orientation="horizontal">
        <ScrollAreaThumb />
      </ScrollAreaScrollbar>
    </ScrollArea>
  );
}
